import "../../editorStyleSheet.css";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import {Stage, Layer} from 'react-konva';
import ImageComponent from "./ImageComponent";
import BoundingBoxComponent from "./BoundingBoxComponent";
import BlocksBoxComponent from "./BlocksBoxComponent";
import { Tally2 } from 'lucide-react';

const EditorCanvasComponent = ({mediaPath, containerWidth, page, pages, setForcePageNumber, zoom, setZoom, brightness, contrast, selectedWordBbox, pdfDimensions, addOrUpdatePages, pageNumber, setHoveredChunkId, hoveredChunkId }) => {

    const containerRef = useRef();
    const [searchParams, setSearchParams] = useSearchParams();
    const [stageWidth, setStageWidth] = useState(0);
    const [stageHeight, setStageHeight] = useState(0);
    const [imageWidth, setImageWidth] = useState(600);
    const [imageHeight, setImageHeight] = useState(750);
    const [naturalSize, setNaturalSize] = useState({width: 0, height: 0});
    const thumbnailContainerRef = useRef(null);
    const displayWidth = naturalSize.width || imageWidth;
    const displayHeight = naturalSize.height || imageHeight;
    const xOffset = (stageWidth  / zoom - displayWidth)  / 2;
    const yOffset = (stageHeight / zoom - displayHeight) / 2;
    const isHand = 'hand';
    let scrollInterval;

    useEffect(() => {
        setImageWidth(pdfDimensions.width)
        setImageHeight(pdfDimensions.height)
    }, [pdfDimensions]);

    useEffect(() => {
        if (pageNumber) {
            const selectedThumbnail = document.querySelector(`.thumbnail:nth-child(${pageNumber})`);
            if (selectedThumbnail) {
                selectedThumbnail.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest',
                });
            }
        }
    }, [pageNumber]);

    const handleZooming = (newZoom) => {
        setZoom(newZoom);
    };

    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            setStageWidth(containerRef.current.offsetWidth);
            setStageHeight(containerRef.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                setStageWidth(width);
                setStageHeight(height);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    const handleSidebarToggle = () => {
        updateDimensions();
    };

    useEffect(() => {
        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    useEffect(() => {
        localStorage.setItem("SidebarStatus", "Collapsed");
        window.addEventListener('sidebarToggled', handleSidebarToggle);
        window.addEventListener('resize', updateDimensions);
        window.addEventListener('configResized', updateDimensions);

        return () => {
            window.removeEventListener('sidebarToggled', handleSidebarToggle);
            window.removeEventListener('resize', updateDimensions);
            window.removeEventListener('configResized', updateDimensions);
        };
    }, [containerWidth]);

    const handleWheel = (e) => {
        e.evt.preventDefault();
        const scaleBy = 1.1;
        const stage = e.target.getStage();
        const oldScale = stage.scaleX();
        const mousePointTo = {
            x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
            y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
        };

        const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

        stage.scale({x: newScale, y: newScale});
        stage.position({
            x: -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale,
            y: -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale,
        });
        stage.batchDraw();
        handleZooming(newScale);
    };

    const pointerShape = () => {
        switch (isHand) {
            case "hand":
                return "pointer";
            case "bounding":
                return "crosshair";
            case "boundingCategory":
                return "auto";
        }
    };

    const startAutoScroll = (direction) => {
        const scrollAmount = 100

        scrollInterval = setInterval(() => {
            if (direction === "left") {
                thumbnailContainerRef.current.scrollLeft -= scrollAmount;
            } else if (direction === "right") {
                thumbnailContainerRef.current.scrollLeft += scrollAmount;
            }
        }, 100);
    };

    const stopAutoScroll = () => {
        clearInterval(scrollInterval);
    };

    const handleThumbnailClick = async (newPage) => {
        setForcePageNumber(newPage)
        searchParams.set("page", newPage);
        setSearchParams(searchParams);
    };

    // Default zoom fits the whole image in the canvas, so large images are not cropped
    useEffect(() => {
        if (stageWidth && stageHeight && displayWidth && displayHeight) {
            setZoom(Math.min(stageWidth / displayWidth, stageHeight / displayHeight) * 0.9);
        }
    }, [stageWidth, stageHeight, displayWidth, displayHeight]);

    return (
        <div className="DigitizationEditorCanvas" ref={containerRef}>
            <div style={{position: 'relative'}}>
                <div style={{
                    position: 'absolute',
                    top: `${stageHeight / 2}px`,
                    right: '-29px',
                    zIndex: 10,
                    pointerEvents: 'none'
                }}>
                    <Tally2 size={40} style={{ color: 'rgb(195 189 189)' }}/>
                </div>
                <Stage
                    width={stageWidth}
                    height={stageHeight}
                    onWheel={handleWheel}
                    scaleX={zoom}
                    scaleY={zoom}
                    draggable={isHand}
                    style={{cursor: pointerShape(), background: '#363537'}}
                >
                    <Layer>
                        <ImageComponent src={mediaPath}
                                        x={xOffset}
                                        y={yOffset}
                                        brightness={brightness}
                                        contrast={contrast}
                                        onLoad={setNaturalSize}
                        />
                        <BoundingBoxComponent
                            selectedWordBbox={selectedWordBbox}
                            xOffset={xOffset} yOffset={yOffset}
                            imageWidth={imageWidth}
                            imageHeight={imageHeight}
                            zoom={zoom}
                            addOrUpdatePages={addOrUpdatePages}
                            pageNumber={pageNumber}
                        />

                        <BlocksBoxComponent
                            xOffset={xOffset} 
                            yOffset={yOffset}
                            setHoveredChunkId={setHoveredChunkId}
                            hoveredChunkId={hoveredChunkId}
                            page={page}
                        />

                    </Layer>
                </Stage>
            </div>
                <div className="thumbnail-navigation">
                    <button
                        onMouseEnter={() => startAutoScroll("left")}
                        onMouseLeave={stopAutoScroll}
                        className="nav-arrow left-arrow"
                    >
                        {"<"}
                    </button>

                    <div className="thumbnail-container" ref={thumbnailContainerRef}>
                        {pages?.map((thumbnail, index) => (
                            <div
                                key={thumbnail.pageNumber}
                                className={`thumbnail ${thumbnail.pageNumber === page ? "active" : ""}`}
                                onClick={() => handleThumbnailClick(thumbnail.pageNumber)}
                            >
                                <img src={thumbnail.src} alt={`Page ${thumbnail.pageNumber}`}/>
                                <div className={`page-number ${thumbnail.pageNumber === page ? "highlighted" : ""}`}>
                                    {`${thumbnail.pageNumber}`}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        onMouseEnter={() => startAutoScroll("right")}
                        onMouseLeave={stopAutoScroll}
                        className="nav-arrow right-arrow"
                    >
                        {">"}
                    </button>
            </div>
        </div>
    );
};

EditorCanvasComponent.propTypes = {
    mediaPath: PropTypes.string,
    containerWidth: PropTypes.string,
    zoom: PropTypes.number,
    pageNumber: PropTypes.number,
    setZoom: PropTypes.func,
    page: PropTypes.object,
    pdfDimensions: PropTypes.object,
    pages: PropTypes.array,
    selectedWordBbox: PropTypes.array,
    setForcePageNumber: PropTypes.func,
    addOrUpdatePages: PropTypes.func,
    brightness: PropTypes.number,
    contrast: PropTypes.number,
    setHoveredChunkId: PropTypes.func,
    hoveredChunkId: PropTypes.string,
};

export default EditorCanvasComponent;
